import { Maybe } from "@administrate/piston-ux/lib/types";
import {
  Course,
  CourseObjective,
  CourseOutcome,
  DocumentObjective,
  DocumentOutcome,
  ExternalObjective,
  ExternalOutcome,
  LearningPathObjective,
  LearningPathOutcome,
  LearningPathRegistration,
  LearningPathResult,
  Registration,
  UrlObjective,
  UrlOutcome,
} from "../generated/lmsTypes";

import { LearningObjectiveProps } from "@administrate/piston-ux/lib/LearningObjective";

export type LearningObjectiveUnion =
  | CourseObjective
  | DocumentObjective
  | UrlObjective
  | LearningPathObjective
  | ExternalObjective
  | null
  | undefined;

export type LearningOutcomeUnion =
  | CourseOutcome
  | DocumentOutcome
  | UrlOutcome
  | LearningPathOutcome
  | ExternalOutcome;

export type RegisterableUnion =
  | LearningPathRegistration
  | Registration
  | null
  | undefined;

export type OutcomeInfo = {
  type: string;
  id: Maybe<string>;
  course: Maybe<Course>;
  title: Maybe<string> | undefined;
  start: string;
  end: string;
};

export const getOutcomeInfo = (outcome: LearningOutcomeUnion) => {
  const info: OutcomeInfo = {
    type: extractOutcomeType(outcome),
    id: outcome.id ? outcome.id : null,
    title: getOutcomeTitle(outcome),
    start: getOutcomeStart(outcome),
    end: getOutcomeEnd(outcome),
    course: null,
  };

  if (outcome.__typename === "CourseOutcome") {
    info.course = outcome.course ? outcome.course : null;
  }
  return info;
};

const getOutcomeTitle = (outcome: LearningOutcomeUnion) => {
  if (outcome.__typename === "CourseOutcome") {
    return outcome.course ? outcome.course.title : "";
  }
  if (outcome.__typename === "LearningPathOutcome") {
    return outcome.learningPath ? outcome.learningPath.name : "";
  }
  if (outcome.__typename === "DocumentOutcome") {
    return outcome.document ? outcome.document.displayName : "";
  }
  if (outcome.__typename === "URLOutcome") {
    return outcome.url;
  }
  if (outcome.__typename === "ExternalOutcome") {
    return outcome.external;
  }
  return "";
};

const getOutcomeStart = (outcome: LearningOutcomeUnion) => {
  if (outcome.__typename === "CourseOutcome") {
    return outcome.course ? outcome.course.start : "";
  }
  if (outcome.__typename === "LearningPathOutcome") {
    return outcome.learningPath ? outcome.learningPath.start : "";
  }
  return "";
};

const getOutcomeEnd = (outcome: LearningOutcomeUnion) => {
  if (outcome.__typename === "CourseOutcome") {
    return outcome.course ? outcome.course.end : "";
  }
  if (outcome.__typename === "LearningPathOutcome") {
    return outcome.learningPath ? outcome.learningPath.end : "";
  }
  return "";
};

const extractOutcomeType = (outcome: LearningOutcomeUnion) =>
  outcome.__typename ? outcome.__typename.replace("Outcome", "") : "";

export const mapObjective = (
  learningObjectiveData: LearningObjectiveUnion,
): LearningObjectiveProps => {
  let objective: LearningObjectiveProps;
  switch (learningObjectiveData?.__typename) {
    case "CourseObjective":
      objective = {
        type: "Course",
        title: learningObjectiveData?.course?.title || "",
        description: learningObjectiveData?.course?.message || undefined,
      };
      break;
    case "DocumentObjective":
      objective = {
        type: "Document",
        title: learningObjectiveData?.document?.displayName || "",
      };
      break;
    case "URLObjective":
      objective = {
        type: "External",
        title: learningObjectiveData.url || "",
        onClick: () =>
          learningObjectiveData.url && window.open(learningObjectiveData.url),
      };
      break;
    case "ExternalObjective":
      objective = {
        type: "External",
        title: learningObjectiveData.name || "",
        description: learningObjectiveData.external || "",
      };
      break;
    case "LearningPathObjective":
      if (!learningObjectiveData.learningPath) {
        console.warn("mapObjective: double nested path without data.");
        objective = {
          type: "Double Nested Path",
          title: "Double Nested Path",
        };
      } else {
        objective = {
          type: "Learning Path",
          title: learningObjectiveData.learningPath.name,
          description:
            learningObjectiveData.learningPath.longDescription || undefined,
        };
      }
      break;
    default:
      objective = {
        title: "",
        type: undefined,
      };
  }
  return objective;
};

export type ObjectiveAvailability = {
  isAvailable: boolean;
  messageOnDisabled: string | null;
};

export const getObjectiveAvailability = (
  objectiveId: Maybe<string> | undefined,
  objectiveAvailabilityMap: Map<string, ObjectiveAvailability>,
  enforceObjectiveOrder: boolean,
  everythingIsDisabled: boolean,
): ObjectiveAvailability => {
  if (everythingIsDisabled) {
    return {
      isAvailable: false,
      messageOnDisabled: null,
    };
  }
  const availabilityFromMap = objectiveId
    ? objectiveAvailabilityMap.get(objectiveId)
    : undefined;
  return (
    availabilityFromMap || {
      isAvailable: !enforceObjectiveOrder,
      messageOnDisabled: null,
    }
  );
};

export const getObjectiveAvailabilityMap = (
  outcomes: (LearningPathResult | CourseOutcome)[],
  objectives: LearningObjectiveUnion[],
  enforceObjectiveOrder: boolean,
): Map<string, ObjectiveAvailability> => {
  const availabilityMap = new Map();

  const groupSizes = new Map<string, number>();
  const groupCompletedSizes = new Map<string, number>();
  objectives.forEach((objective) => {
    const groupId = objective?.group?.id;
    if (groupId && objective?.id) {
      groupSizes.set(groupId, (groupSizes.get(groupId) ?? 0) + 1);
      const result = findResultForObjective(outcomes, objective.id);
      if (result?.completed) {
        groupCompletedSizes.set(
          groupId,
          (groupCompletedSizes.get(groupId) ?? 0) + 1,
        );
      }
    }
  });

  // The objectives are walked in display order as a sequence of "runs": a
  // contiguous stretch of one option group's members, or a single ungrouped
  // objective. All members of a run share the availability the path had when
  // the run was reached, so objectives inside an option group can be taken in
  // any order. A run only gates the runs after it: an ungrouped objective must
  // be completed, a group needs numRequired of its members completed.
  let gateOpen = true;
  let entryGate = true;
  let previousGroupId: string | null = null;

  objectives.forEach((objective) => {
    if (!objective || !objective.id) {
      return;
    }

    const group = objective.group;
    const groupId = group?.id ?? null;

    const startsNewRun = groupId === null || groupId !== previousGroupId;
    if (startsNewRun) {
      entryGate = gateOpen;
      // numRequired can exceed the member count for a nested-path step (it
      // counts the nested path's required objectives) and 0 means the group
      // is optional — cap it, mirroring the backend's min() when it computes
      // learner progress.
      const satisfied = groupId
        ? (groupCompletedSizes.get(groupId) ?? 0) >=
          Math.min(group?.numRequired ?? 0, groupSizes.get(groupId) ?? 0)
        : !!findResultForObjective(outcomes, objective.id)?.completed;
      gateOpen = gateOpen && satisfied;
    }
    previousGroupId = groupId;

    const availability: ObjectiveAvailability = {
      isAvailable: !enforceObjectiveOrder || entryGate,
      messageOnDisabled: null,
    };
    availabilityMap.set(objective.id, availability);
  });

  return availabilityMap;
};

function findResultForObjective(
  outcomes: (LearningPathResult | CourseOutcome)[],
  objectiveId: string,
) {
  return outcomes.find((outcome) => outcome.objectiveId === objectiveId);
}
